/* eslint-disable @typescript-eslint/consistent-type-imports */
import React from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { SelectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';

import {
	ANALYTICS_ACTION,
	ANALYTICS_SOURCE,
	ANALYTICS_SUBJECT,
	ANALYTICS_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import { CreateSnippetModal } from '../modals/CreateSnippetModal.tsx';

export type UseHandleCreateSnippetProps = {
	contentId: string;
	spaceId: string;
	spaceKey: string;
	editorAPIRef?: React.RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
};

/**
 * Hook that returns a handler for creating a snippet from the current editor selection.
 * Pass editorAPIRef from the caller (e.g. a ref holding the fullPageEditorPresetAPI).
 */
export const useHandleCreateSnippet = ({
	contentId,
	spaceId,
	spaceKey,
	editorAPIRef,
}: UseHandleCreateSnippetProps): (() => void) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showModal } = useDialogs();

	return () => {
		const editorAPI = editorAPIRef?.current;
		if (!editorAPI) {
			return;
		}

		const actions = editorAPI.selectionExtension?.actions;
		const document = actions?.getDocumentFromSelection?.();
		const docAdf = document?.selectedNodeAdf;

		// getDocumentFromSelection will always return adf of type 'doc',
		// so strip this doc wrapper for the saved content
		const adfContent = docAdf?.content?.filter((content: unknown) => content !== undefined);
		if (!adfContent || adfContent.length === 0) {
			return;
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: ANALYTICS_SOURCE,
				actionSubject: ANALYTICS_SUBJECT.BUTTON,
				action: ANALYTICS_ACTION.CLICKED,
				actionSubjectId: ANALYTICS_SUBJECT_ID.CREATE_SNIPPET,
				type: 'ui',
				attributes: {
					selectionLength: JSON.stringify(adfContent)?.length,
				},
			},
		}).fire();

		showModal(CreateSnippetModal, {
			adf: docAdf,
			contentId,
			spaceId,
			spaceKey,
			editorAPI,
		});
	};
};
